import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';

import {
  GET_TREATMENT_GROUP_FOR_PERSON_QUERY_KEY,
  GET_TREATMENT_GROUP_MEMBERSHIPS_BY_PERSON_QUERY_KEY,
  GET_TREATMENT_GROUP_QUERY_KEY,
  GET_TREATMENT_GROUPS_FOR_PERSON_QUERY_KEY,
  GET_TREATMENT_GROUPS_QUERY_KEY
} from '@/constants/treatmentGroups';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import TreatmentGroupRepository from '@/repositories/TreatmentGroupRepository';
import { QueryParams } from '@/types/query';
import {
  TreatmentGroup,
  TreatmentGroupPayload,
  TreatmentGroupSessionPayload
} from '@/types/treatmentGroup';
import { getThisWeeksMonday, todayLocalDateString } from '@/utils/dates';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

export const useTreatmentGroups = (
  params: QueryParams,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const treatmentGroupRepository = TreatmentGroupRepository();

  return useAPI({
    queryKey: [GET_TREATMENT_GROUPS_QUERY_KEY, params],
    queryFn: async () => {
      return await treatmentGroupRepository.list(params);
    },
    enabled
  });
};

export const useTreatmentGroup = (id?: string) => {
  const treatmentGroupRepository = TreatmentGroupRepository();

  return useAPI({
    queryKey: [GET_TREATMENT_GROUP_QUERY_KEY, id],
    queryFn: async () => {
      return await treatmentGroupRepository.get(id as string);
    },
    enabled: !!id
  });
};

export const useTreatmentGroupsForPerson = (personId?: string, params: QueryParams = {}) => {
  const treatmentGroupRepository = TreatmentGroupRepository();

  return useAPI({
    queryKey: [GET_TREATMENT_GROUPS_FOR_PERSON_QUERY_KEY, personId, params],
    queryFn: async () => {
      return await treatmentGroupRepository.listForPerson(personId as string, params);
    },
    enabled: !!personId
  });
};

export const useTreatmentGroupForPerson = (
  groupId?: string,
  personId?: string,
  timezone?: string
) => {
  const treatmentGroupRepository = TreatmentGroupRepository();

  return useAPI({
    queryKey: [GET_TREATMENT_GROUP_FOR_PERSON_QUERY_KEY, groupId, personId, timezone],
    queryFn: async () => {
      return await treatmentGroupRepository.getForPerson(
        groupId as string,
        personId as string,
        timezone
      );
    },
    enabled: !!groupId && !!personId
  });
};

export const useCreateTreatmentGroup = () => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const treatmentGroupRepository = TreatmentGroupRepository();

  return useMutation({
    mutationFn: (data: Partial<TreatmentGroupPayload>) => treatmentGroupRepository.create(data),
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Treatment group created successfully' });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUPS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUPS_FOR_PERSON_QUERY_KEY] })
      ]);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
};

export const useUpdateTreatmentGroup = () => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const treatmentGroupRepository = TreatmentGroupRepository();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TreatmentGroupPayload> }) =>
      treatmentGroupRepository.update(id, data),
    onSuccess: async (_data, { id }) => {
      addMessage({ level: 'success', text: 'Treatment group updated successfully' });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUPS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUPS_FOR_PERSON_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUP_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUP_FOR_PERSON_QUERY_KEY, id] })
      ]);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
};

export const useEndTreatmentGroup = () => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const treatmentGroupRepository = TreatmentGroupRepository();

  return useMutation({
    mutationFn: ({ id, endDate }: { id: string; endDate: string }) =>
      treatmentGroupRepository.end(id, endDate),
    onSuccess: async (_data, { id }) => {
      addMessage({ level: 'success', text: 'Treatment group ended successfully' });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUPS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUPS_FOR_PERSON_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUP_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUP_FOR_PERSON_QUERY_KEY, id] })
      ]);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
};

// Whether a membership is still active as of today, going purely off
// end_on (a group-editor-local date, unlike hasGroupEnded's timezone-aware
// comparison) -- good enough for deciding which of the source group's
// members are eligible to be copied over.
const isActiveMembership = (endOn: string | null) => !endOn || endOn >= todayLocalDateString();

// The viewset's update() is a full PUT, not a PATCH -- every required
// TreatmentGroupPayload field has to be resent, not just the members array,
// or the serializer 400s on the fields we omitted. Mirrors what
// TreatmentGroupForm's toPayload sends when an existing group is saved
// unchanged, just carrying the destination's own current values through
// instead of form state.
const toSessionPayload = (
  session: TreatmentGroup['sessions'][number]
): TreatmentGroupSessionPayload => ({
  id: session.id,
  day_of_week: session.day_of_week,
  effective_from: session.effective_from,
  ...(session.effective_until && { effective_until: session.effective_until }),
  staff_id: session.staff_id,
  start_time: session.start_time
});

export interface MoveTreatmentGroupMembersInput {
  destinationGroup: TreatmentGroup;
  sourceGroup: TreatmentGroup;
}

export const useMoveTreatmentGroupMembers = () => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const treatmentGroupRepository = TreatmentGroupRepository();

  return useMutation({
    mutationFn: async ({ destinationGroup, sourceGroup }: MoveTreatmentGroupMembersInput) => {
      const existingPersonIds = new Set(destinationGroup.members.map((member) => member.person_id));
      // The members array is the destination's full desired roster, so its
      // current members have to be resent alongside the copied-over ones --
      // omitting partial_session_letters leaves each one's session
      // selection untouched.
      const existingMembers = destinationGroup.members.map((member) => ({
        id: member.id,
        person_id: member.person_id,
        end_on: member.end_on,
        start_on: member.start_on
      }));
      // Mirrors TreatmentGroupForm's toPayload default for a brand-new
      // membership row.
      const startOn = destinationGroup.is_draft ? getThisWeeksMonday() : todayLocalDateString();
      const copiedMembers = sourceGroup.members
        .filter((member) => isActiveMembership(member.end_on))
        .filter((member) => !existingPersonIds.has(member.person_id))
        // No id (new row) and no partial_session_letters (omitted ->
        // enrolled in every session), per the destination group.
        .map((member) => ({ person_id: member.person_id, start_on: startOn }));

      const payload: TreatmentGroupPayload = {
        accepting_new_patients: destinationGroup.accepting_new_patients,
        appointment_type_id: destinationGroup.appointment_type_id,
        capacity: destinationGroup.capacity,
        end_date: destinationGroup.end_date,
        is_draft: destinationGroup.is_draft,
        members: [...existingMembers, ...copiedMembers],
        name: destinationGroup.name,
        sessions: destinationGroup.sessions.map(toSessionPayload),
        start_date: destinationGroup.start_date,
        timezone: destinationGroup.timezone
      };

      const group = await treatmentGroupRepository.update(destinationGroup.id, payload);
      return { copiedCount: copiedMembers.length, group };
    },
    onSuccess: async (_data, { destinationGroup, sourceGroup }) => {
      // No success toast here -- the drawer shows a richer inline success
      // banner in its place (see TreatmentGroupView).
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUPS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUPS_FOR_PERSON_QUERY_KEY] }),
        queryClient.invalidateQueries({
          queryKey: [GET_TREATMENT_GROUP_QUERY_KEY, destinationGroup.id]
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_TREATMENT_GROUP_QUERY_KEY, sourceGroup.id]
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_TREATMENT_GROUP_FOR_PERSON_QUERY_KEY, destinationGroup.id]
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_TREATMENT_GROUP_FOR_PERSON_QUERY_KEY, sourceGroup.id]
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_TREATMENT_GROUP_MEMBERSHIPS_BY_PERSON_QUERY_KEY]
        })
      ]);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
};

export const useDeleteTreatmentGroup = () => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const treatmentGroupRepository = TreatmentGroupRepository();

  return useMutation({
    mutationFn: (id: string) => treatmentGroupRepository.delete(id),
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Treatment group deleted successfully' });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUPS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUPS_FOR_PERSON_QUERY_KEY] })
      ]);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
};

export default useTreatmentGroups;
